import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchStaffDashboard } from '../../api/staff';
import StaffMembershipInvites from '../../components/staff/StaffMembershipInvites';
import StaffWeekSchedule from '../../components/staff/StaffWeekSchedule';
import DashboardShell from '../../components/ui/DashboardShell';
import { useAuth } from '../../context/AuthContext';
import { activityIcon, describeActivity } from '../../utils/activityLabels';

const SalonStaffDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const hour = new Date().getHours();
  const greeting =
    hour < 12
      ? t('salon.greeting.morning')
      : hour < 17
        ? t('salon.greeting.afternoon')
        : t('salon.greeting.evening');

  const { data, isLoading } = useQuery({
    queryKey: ['staff-dashboard'],
    queryFn: fetchStaffDashboard,
  });

  const pendingItems = data?.pendingItems || [];
  const recentActivity = data?.recentActivity || [];

  return (
    <DashboardShell title={`${greeting}, ${user?.firstName}`} subtitle={t('staff.dashboard.subtitle')}>
      <StaffMembershipInvites />
      {isLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}

      {!isLoading && (
        <StaffWeekSchedule
          weekRange={data?.weekRange}
          summary={data?.summary}
          pendingAssignments={data?.pendingAssignments}
          weekAppointments={data?.weekAppointments}
        />
      )}

      {pendingItems.length > 0 && (
        <div className="mb-8 grid gap-3 sm:grid-cols-2">
          {pendingItems.map((item) => (
            <Link
              key={item.type}
              to={
                item.type === 'reviews'
                  ? '/salon/reviews'
                  : item.type === 'orders'
                    ? '/staff/orders'
                    : '/staff/appointments'
              }
              className="rounded-xl border border-amber-800/40 bg-amber-950/20 p-4 hover:border-amber-700"
            >
              <p className="text-sm font-medium text-amber-300">
                {item.type === 'reviews'
                  ? t('staff.dashboard.pendingReviews', { count: item.count })
                  : item.type === 'appointments'
                    ? t('staff.dashboard.pendingAppointments', { count: item.count })
                    : item.type === 'orders'
                      ? t('staff.dashboard.pendingOrders', { count: item.count })
                      : t('staff.dashboard.pendingAssignments', { count: item.count })}
              </p>
              <p className="mt-1 text-2xl font-semibold">{item.count}</p>
              <p className="mt-1 text-xs text-amber-200/60">{t('staff.dashboard.needsAttention')}</p>
            </Link>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/staff/appointments" className="text-sm text-glow-400 hover:underline">
          {t('staff.dashboard.viewAllAppointments')}
        </Link>
      </div>

      <div className="mt-8 rounded-xl border border-zinc-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
        <h3 className="font-medium">{t('staff.dashboard.recentActivity')}</h3>
        <ul className="mt-4 space-y-3">
          {recentActivity.map((log) => (
            <li key={log._id} className="flex gap-3 text-sm">
              <span>{activityIcon(log.action)}</span>
              <div>
                <p>{describeActivity(log)}</p>
                <p className="text-xs text-zinc-600 dark:text-zinc-500">
                  {new Date(log.createdAt).toLocaleString()}
                </p>
              </div>
            </li>
          ))}
          {recentActivity.length === 0 && (
            <p className="text-sm text-zinc-600 dark:text-zinc-500">{t('staff.dashboard.noRecentActions')}</p>
          )}
        </ul>
      </div>
    </DashboardShell>
  );
};

export default SalonStaffDashboard;
